import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ProjectContextStore } from './project-context.store';
import { PermissionsStore } from '@core/permissions/permissions.store';

const PROJECT_ID_HEADER = 'X-Project-Id';

/**
 * Session and project-selection calls, which run before a project is resolved
 * (`connect/token`, `api/User/me`) or while it is being changed
 * (`SetSelectedProject`, which names the new project in its own query string)
 * or abandoned (`connect/logout`). Matched as substrings of the request URL.
 */
const URL_EXCEPTIONS = [
  'connect/token',
  'api/User/me',
  'UserProjectRole/SetSelectedProject',
  'connect/logout'
];

/**
 * The backend requires every project-scoped request to name the project it acts
 * on, so this stamps `X-Project-Id` with the active project.
 *
 * The id is read from `ProjectContextStore` on every request rather than
 * captured once, so a project switch takes effect on the very next call with no
 * reload. While no project is selected the header is left off rather than sent
 * empty.
 *
 * A super admin sends no header: the role is user-level and reaches every
 * project, so the backend does not require one. The flag comes from
 * `PermissionsStore`, set by `/api/User/me` and cleared on every session ending.
 */
export const projectContextInterceptor: HttpInterceptorFn = (req, next) => {
  const projectContextStore = inject(ProjectContextStore);
  const permissionsStore = inject(PermissionsStore);

  const selectedProjectId = projectContextStore.activeProjectId();
  const isSuperAdmin = permissionsStore.isSuperAdmin();

  if (isSuperAdmin || !selectedProjectId) return next(req);
  if (URL_EXCEPTIONS.some(url => req.url.includes(url))) return next(req);

  return next(req.clone({
    headers: req.headers.set(PROJECT_ID_HEADER, selectedProjectId.toString())
  }));
};
